import React, { forwardRef, useImperativeHandle, useRef } from "react"

import { Button } from "@coval/ui"
import { Paperclip } from "lucide-react"

import { formatBytes } from "@/utils/formatting/format-bytes"

import { TEST_SET_ATTACHMENT_LIMITS } from "../../constants"

export interface AttachedFile {
  id: string
  file: File
  name: string
  size: number
  type: string
}

interface FileUploadButtonProps {
  onFilesSelected: (files: AttachedFile[]) => void
  existingFiles?: AttachedFile[]
  existingFileCount?: number
  existingTotalSize?: number
  onValidationError?: (error: string | null) => void
}

export const FileUploadButton = forwardRef<
  HTMLButtonElement,
  FileUploadButtonProps
>(
  (
    {
      onFilesSelected,
      onValidationError,
      existingFileCount = 0,
      existingTotalSize = 0,
    },
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    useImperativeHandle(ref, () => ({
      ...buttonRef.current!,
      click: () => fileInputRef.current?.click(),
    }))

    const handleFileUpload = () => {
      fileInputRef.current?.click()
    }

    const resetInputValue = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }

    const hasAllowedExtension = (fileName: string) => {
      const extension = fileName.split(".").pop()?.toLowerCase()
      if (!extension) {
        return false
      }

      return TEST_SET_ATTACHMENT_LIMITS.allowedExtensions.some(
        (allowedExt) => allowedExt === `.${extension}`,
      )
    }

    const isAllowedType = (file: File) => {
      if (
        file.type &&
        (
          TEST_SET_ATTACHMENT_LIMITS.allowedMimeTypes as readonly string[]
        ).includes(file.type)
      ) {
        return true
      }

      return hasAllowedExtension(file.name)
    }

    const reportError = (message: string) => {
      onValidationError?.(message)
      resetInputValue()
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }

      const selectedFiles = Array.from(files)
      const maxFiles = TEST_SET_ATTACHMENT_LIMITS.maxFiles

      // Check total file count limit
      if (existingFileCount + selectedFiles.length > maxFiles) {
        reportError(`You can attach up to ${maxFiles} files.`)
        return
      }

      // Validate each file
      for (const file of selectedFiles) {
        if (!isAllowedType(file)) {
          reportError(
            `Unsupported file type. Allowed formats: ${TEST_SET_ATTACHMENT_LIMITS.allowedExtensions.join(", ")}`,
          )
          return
        }

        if (file.size > TEST_SET_ATTACHMENT_LIMITS.maxFileSizeBytes) {
          reportError(
            `Each file must be smaller than ${formatBytes(TEST_SET_ATTACHMENT_LIMITS.maxFileSizeBytes)}.`,
          )
          return
        }
      }

      // Check total size limit
      const selectedSize = selectedFiles.reduce(
        (sum, file) => sum + file.size,
        0,
      )
      if (
        existingTotalSize + selectedSize >
        TEST_SET_ATTACHMENT_LIMITS.maxTotalSizeBytes
      ) {
        reportError(
          `Attached files must be smaller than ${formatBytes(TEST_SET_ATTACHMENT_LIMITS.maxTotalSizeBytes)} in total.`,
        )
        return
      }

      const newFiles: AttachedFile[] = selectedFiles.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      }))

      onValidationError?.(null)
      onFilesSelected(newFiles)

      // Reset the input so the same file can be selected again
      resetInputValue()
    }

    return (
      <>
        <Button
          ref={buttonRef}
          icon
          variant="ghost"
          className="text-coval-text-muted hover:text-coval-text min-w-5 w-5 h-5 p-0"
          onClick={handleFileUpload}
          tooltip="Attach files"
        >
          <Paperclip className="w-3.5 h-3.5" />
        </Button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          className="hidden"
          accept={TEST_SET_ATTACHMENT_LIMITS.allowedExtensions.join(",")}
        />
      </>
    )
  },
)

FileUploadButton.displayName = "FileUploadButton"
